// src/sim/geometry.ts — pure geometry helpers for circle-vs-shape collision.
//
// Used by movement.ts for wall / obstacle / fence / trunk collision resolution.
//
// Invariants:
//   - Zero runtime dependencies — pure TypeScript computation only.
//   - No Phaser, no DOM, no timers (ESLint sim-boundary enforces).
//   - All functions are deterministic and side-effect-free.
//   - Squared-distance comparisons use multiplication (not Math.pow) for
//     IEEE-754 determinism across JS engines; avoid Math.hypot (not
//     guaranteed correctly-rounded across engines — same reasoning as the
//     frictionless-slide fix in movement.ts).
//
// Phase 08.1 Item 1: shape-precise movement collision.

import type { Vec2 } from '../shared/types/intent';

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * Fence collision half-width in world pixels.
 *
 * Each fence segment occupies a capsule of total width 2 × FENCE_COLLISION_HALF_WIDTH,
 * matching the rasterisation thickness used by buildMovementGrid (0.5 × tileWidth
 * on each side = 16 px for 32 px tiles). Applied as (characterRadius + halfWidth)
 * in the circle-vs-capsule test.
 */
export const FENCE_COLLISION_HALF_WIDTH = 16;

// ─── Primitives ───────────────────────────────────────────────────────────────

/**
 * Squared distance from point (px, py) to the closest point on segment
 * (ax, ay) → (bx, by).
 *
 * Used by circleOverlapsPolygon (edge-distance test) and circleOverlapsFence
 * (capsule test). Returns squared distance to avoid the sqrt for comparisons.
 */
export function pointToSegmentDistSq(
  px: number, py: number,
  ax: number, ay: number,
  bx: number, by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    // Degenerate segment (zero length) — distance to either endpoint.
    const ex = px - ax;
    const ey = py - ay;
    return ex * ex + ey * ey;
  }
  // t = projection scalar clamped to [0, 1].
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  const qx = ax + t * dx;
  const qy = ay + t * dy;
  const fx = px - qx;
  const fy = py - qy;
  return fx * fx + fy * fy;
}

/**
 * Closest point on segment (ax, ay) → (bx, by) to the point (px, py).
 *
 * Companion to pointToSegmentDistSq — returns the actual foot-of-perpendicular
 * (clamped to the segment) used by the slide resolver to derive a fence/segment
 * collision normal (direction from this point to the circle centre).
 */
export function closestPointOnSegment(
  px: number, py: number,
  ax: number, ay: number,
  bx: number, by: number,
): Vec2 {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return { x: ax, y: ay };
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  return { x: ax + t * dx, y: ay + t * dy };
}

// ─── Point-in-polygon ─────────────────────────────────────────────────────────

/**
 * Ray-casting point-in-polygon test (Jordan curve theorem).
 *
 * Returns true when (px, py) is strictly inside the closed polygon.
 * Points exactly on an edge are implementation-defined — sub-pixel precision
 * is sufficient for all call sites in this codebase.
 */
function pointInPolygon(px: number, py: number, verts: readonly Vec2[]): boolean {
  let inside = false;
  const n = verts.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = verts[i]!.x;
    const yi = verts[i]!.y;
    const xj = verts[j]!.x;
    const yj = verts[j]!.y;
    if (
      (yi > py) !== (yj > py) &&
      px < ((xj - xi) * (py - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }
  return inside;
}

// ─── Circle-vs-polygon ────────────────────────────────────────────────────────

/**
 * True when a circle at (cx, cy) with radius r overlaps a SOLID closed polygon.
 *
 * Two cases:
 *   1. Circle centre is INSIDE the polygon (point-in-polygon ray-cast).
 *   2. The closest point on any polygon edge is within r of the circle centre
 *      (edge-distance test using the squared-distance primitive above).
 *
 * Performance: O(V) per call, where V = vertex count. With 18-vertex average
 * for wall polygons and 31 wall polygons, the worst-case per character per
 * axis-step is 31 × 18 ≈ 558 ops — negligible at 30 Hz × 4 entities.
 *
 * Broad-phase (caller): call only when the circle AABB overlaps the polygon
 * AABB for further savings if ever needed. Not required for current entity counts.
 */
export function circleOverlapsPolygon(
  cx: number,
  cy: number,
  r: number,
  verts: readonly Vec2[],
): boolean {
  if (verts.length < 3) return false;

  // Case 1: centre inside polygon — solid fill blocks the circle entirely.
  if (pointInPolygon(cx, cy, verts)) return true;

  // Case 2: any edge within r — circle bulges into the polygon boundary.
  const rSq = r * r;
  const n = verts.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    if (
      pointToSegmentDistSq(
        cx, cy,
        verts[j]!.x, verts[j]!.y,
        verts[i]!.x, verts[i]!.y,
      ) < rSq
    ) {
      return true;
    }
  }

  return false;
}

// ─── Circle-vs-fence (capsule) ────────────────────────────────────────────────

/**
 * True when a circle at (cx, cy) with radius r overlaps a fence segment capsule.
 *
 * A fence is a thin line segment. The collision shape is the Minkowski sum of
 * the segment with a disc of radius `halfWidth`, i.e. a capsule. The circle
 * overlaps the capsule iff the distance from the circle centre to the segment
 * is less than (r + halfWidth).
 *
 * `halfWidth` should be FENCE_COLLISION_HALF_WIDTH (16 px for 32-px tiles),
 * matching the rasterisation thickness in buildMovementGrid.
 */
export function circleOverlapsFence(
  cx: number,
  cy: number,
  r: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  halfWidth: number,
): boolean {
  const combined = r + halfWidth;
  return pointToSegmentDistSq(cx, cy, ax, ay, bx, by) < combined * combined;
}

// ─── Circle-vs-trunk ellipse ─────────────────────────────────────────────────

/**
 * True when a circle at (cx, cy) with radius r overlaps a trunk ellipse at
 * (ecx, ecy) with semi-axes (erx, ery).
 *
 * Algorithm (inflated-ellipse approximation):
 *   Inflate the trunk ellipse semi-axes by r: test whether the circle centre
 *   lies inside the inflated ellipse via the standard normalised-quadrant test:
 *     ((cx - ecx) / (erx + r))² + ((cy - ecy) / (ery + r))² < 1
 *
 *   This approximates the true Minkowski sum (which is an implicit quartic).
 *   For small r relative to erx/ery (character radius 8 px vs trunk radius
 *   ≥ 3 px — trunk is often 5–20 px), the approximation is within 5% and
 *   imperceptible in gameplay.
 */
export function circleOverlapsTrunk(
  cx: number,
  cy: number,
  r: number,
  ecx: number,
  ecy: number,
  erx: number,
  ery: number,
): boolean {
  if (erx <= 0 || ery <= 0) return false;
  const dx = (cx - ecx) / (erx + r);
  const dy = (cy - ecy) / (ery + r);
  return dx * dx + dy * dy < 1;
}

// ─── Collision normals (for tangent-projection sliding) ─────────────────────────
//
// These return the UNIT direction a circle at (cx, cy) should be pushed to exit /
// avoid a solid shape — the shape's outward normal at the point of contact. The
// slide resolver in movement.ts sums these across all overlapping shapes and
// removes the into-wall component of the intended motion, so the character slides
// smoothly along walls at ANY angle (fixes the diagonal-wall "jump"). Callers
// only invoke these when the corresponding circleOverlaps* test is already true.
//
// Determinism: normalisation uses Math.sqrt (IEEE-754 correctly-rounded across
// engines) — never Math.hypot — matching the sim-purity invariant above.

/**
 * Unit outward push-normal for a circle centred at (cx, cy) against a SOLID
 * closed polygon. Points AWAY from the polygon when the centre is outside, and
 * toward the nearest exit when the centre is inside (so a pushed-in circle is
 * ejected the short way out). Returns null for a degenerate polygon (<3 verts)
 * or when the centre coincides exactly with the nearest boundary point.
 */
export function polygonPushNormal(
  cx: number,
  cy: number,
  verts: readonly Vec2[],
): Vec2 | null {
  const n = verts.length;
  if (n < 3) return null;

  // Nearest point on the polygon boundary to the circle centre.
  let bestDistSq = Infinity;
  let qx = 0;
  let qy = 0;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const p = closestPointOnSegment(cx, cy, verts[j]!.x, verts[j]!.y, verts[i]!.x, verts[i]!.y);
    const dx = cx - p.x;
    const dy = cy - p.y;
    const d = dx * dx + dy * dy;
    if (d < bestDistSq) {
      bestDistSq = d;
      qx = p.x;
      qy = p.y;
    }
  }

  // Outside → push away from the boundary (centre − nearest). Inside → push
  // toward the boundary (nearest − centre) to eject the short way out.
  const inside = pointInPolygon(cx, cy, verts);
  const nx = inside ? qx - cx : cx - qx;
  const ny = inside ? qy - cy : cy - qy;
  const len = Math.sqrt(nx * nx + ny * ny);
  if (len === 0) return null;
  return { x: nx / len, y: ny / len };
}

/**
 * Unit outward push-normal for a circle centred at (cx, cy) against a trunk
 * ellipse at (ecx, ecy) with semi-axes (erx, ery). Uses the ellipse implicit
 * gradient ((cx−ecx)/erx², (cy−ecy)/ery²), which points outward along the true
 * surface normal — good enough for the sub-pixel per-tick slide. Returns null
 * for a degenerate ellipse or a centre exactly on the ellipse centre.
 */
export function trunkPushNormal(
  cx: number,
  cy: number,
  ecx: number,
  ecy: number,
  erx: number,
  ery: number,
): Vec2 | null {
  if (erx <= 0 || ery <= 0) return null;
  const nx = (cx - ecx) / (erx * erx);
  const ny = (cy - ecy) / (ery * ery);
  const len = Math.sqrt(nx * nx + ny * ny);
  if (len === 0) return null;
  return { x: nx / len, y: ny / len };
}

// ─── Circle-vs-boundary (containment) ───────────────────────────────────────────
//
// The `boundary` polygon is a CONTAINER: the character circle must stay fully
// INSIDE it (the opposite of a solid wall, which the circle must stay outside).
// Used for the out-of-bounds edge on the real map (Phase 08.1 Item 3).

/**
 * Squared distance from (px, py) to the nearest point on the polygon boundary
 * (its closest edge). Shared by the containment test and its push-normal.
 */
function nearestEdgeDistSq(px: number, py: number, verts: readonly Vec2[]): number {
  let best = Infinity;
  const n = verts.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const d = pointToSegmentDistSq(px, py, verts[j]!.x, verts[j]!.y, verts[i]!.x, verts[i]!.y);
    if (d < best) best = d;
  }
  return best;
}

/**
 * True when a circle at (cx, cy) with radius r lies ENTIRELY inside the closed
 * boundary polygon — i.e. its centre is inside AND no edge is within r of the
 * centre. When this is false the circle has crossed (or is about to cross) the
 * boundary and movement must be blocked.
 *
 * Degenerate polygons (<3 verts) are treated as "no boundary" → always inside
 * (unbounded), so a missing/partial boundary never traps the player.
 */
export function circleInsideBoundary(
  cx: number,
  cy: number,
  r: number,
  verts: readonly Vec2[],
): boolean {
  if (verts.length < 3) return true;
  if (!pointInPolygon(cx, cy, verts)) return false;
  return nearestEdgeDistSq(cx, cy, verts) >= r * r;
}

/**
 * Unit push-normal that moves a circle back INSIDE the boundary polygon — the
 * exact inverse of polygonPushNormal (a container pushes inward where a solid
 * pushes outward). Points from the nearest boundary edge toward the interior.
 * Returns null for a degenerate polygon or a centre coincident with its nearest
 * boundary point. Caller invokes only when circleInsideBoundary is false.
 */
export function boundaryPushNormal(
  cx: number,
  cy: number,
  verts: readonly Vec2[],
): Vec2 | null {
  const out = polygonPushNormal(cx, cy, verts);
  if (out === null) return null;
  return { x: -out.x, y: -out.y };
}
